import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import type { EvaluationResponse } from "../gen/models";
import { ThreadHeader } from "../components/thread/ThreadHeader";
import { ThreadInputBar } from "../components/thread/ThreadInputBar";
import { ThreadMessagesStream } from "../components/thread/ThreadMessagesStream";
import { ThreadDetailModals } from "../components/thread/ThreadDetailModals";
import { useThreadData } from "../components/thread/useThreadData";
import { useThreadMessenger } from "../components/thread/useThreadMessenger";
import { useThreadBatchModals } from "../components/thread/useThreadBatchModals";
import { useCommentEditor } from "../components/thread/useCommentEditor";
import { useInPlaceComposer } from "../components/thread/useInPlaceComposer";
import { useToast } from "../context/ToastContext";
import type { InspectionTarget } from "../components/thread/GovernanceInspectionModal";

export function ThreadDetail() {
  const { thread_id } = useParams<{ thread_id: string }>();
  const { showToast } = useToast();

  // In-situ inspection modal state
  const [inspectionTarget, setInspectionTarget] = useState<InspectionTarget | null>(null);

  const handleInspectRule = (ruleId: string, rule?: any) => {
    setInspectionTarget({ type: "rule", id: ruleId, initialData: rule });
  };

  const handleInspectKnowledge = (itemId: string, item?: any) => {
    setInspectionTarget({ type: "knowledge", id: itemId, initialData: item });
  };

  const handleInspectGuardrail = (guardrailId: string, guardrail?: any) => {
    setInspectionTarget({ type: "guardrail", id: guardrailId, initialData: guardrail });
  };

  const handleInspectChunk = (chunkId: string) => {
    setInspectionTarget({ type: "chunk", id: chunkId });
  };

  // Drawers State
  const [isEvalDrawerOpen, setIsEvalDrawerOpen] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState<EvaluationResponse | null>(null);
  const [evalDrawerMessageId, setEvalDrawerMessageId] = useState<string | null>(null);
  const [evalDrawerRunId, setEvalDrawerRunId] = useState<string | null>(null);
  const [evalDrawerIsHumanMode, setEvalDrawerIsHumanMode] = useState(false);
  const [isSimulatorOpen, setIsSimulatorOpen] = useState(false);
  const [simulatorTurnId, setSimulatorTurnId] = useState<string | undefined>();
  const [simulatorInquiry, setSimulatorInquiry] = useState<string | undefined>();
  const [simulatorBaseline, setSimulatorBaseline] = useState<string | undefined>();

  // Highlighting interaction state
  const [activeHighlightedCommentId, setActiveHighlightedCommentId] = useState<string | null>(null);

  // 1. Thread Data Hook
  const {
    thread,
    messages,
    setMessages,
    scorecard,
    evaluations,
    comments,
    setComments,
    registerDeletedCommentId,
    commentBatchFilter,
    setCommentBatchFilter,
    activeBatches,
    setActiveBatches,
    allBatches,
    setAllBatches,
    activeBatchId,
    setActiveBatchId,
    telemetrySummary,
    runsMap,
    loading,
    isRefreshing,
    error,
    loadThreadData,
    sortMessagesChronologically,
    activeBatch,
    referencedRules,
    referencedKnowledge,
    guardrailsList,
    agentInfo,
    compositeScore,
    isFlagged,
    hasHallucination,
  } = useThreadData({
    threadId: thread_id,
    onAgentReplied: () => setIsWaitingForAgent(false),
  });

  // 2. Thread Chat Messenger Hook
  const {
    inputText,
    setInputText,
    sending,
    isWaitingForAgent,
    setIsWaitingForAgent,
    handleSendMessage,
  } = useThreadMessenger({
    thread,
    messages,
    setMessages,
    sortMessagesChronologically,
    loadThreadData,
    showToast,
  });

  // 3. Batch Modals & Assignment Hook
  const {
    isBatchAssignModalOpen,
    setIsBatchAssignModalOpen,
    openBatchAssignModal,
    handleCloseBatchAssignModal,
    effectiveEvaluations,
    handleAssignDirectToActiveBatch,
    isActiveBatchModalOpen,
    targetBatchId,
    setTargetBatchId,
    newBatchTitle,
    setNewBatchTitle,
    creatingBatchInline,
    setCreatingBatchInline,
    assigningEvaluations,
    assignAllEvaluationsInline,
    setAssignAllEvaluationsInline,
    ensureActiveBatch,
    handleAssignEvaluationsToBatch,
    handleCreateActiveBatch,
    handleSelectActiveBatch,
    handleCloseActiveBatchModal,
  } = useThreadBatchModals({
    evaluations,
    activeBatches,
    activeBatchId,
    setActiveBatchId,
    setActiveBatches,
    setAllBatches,
    loadThreadData,
    showToast,
  });

  // 4. Comment Editor Hook
  const {
    editingCommentId,
    editingText,
    editingRuleIds,
    editingKnowledgeIds,
    savingEdit,
    mentionType: editorMentionType,
    mentionQuery: editorMentionQuery,
    startEdit,
    cancelEdit,
    handleEditTextChange,
    handleSelectMentionRule: handleEditorSelectMentionRule,
    handleSelectMentionKnowledge: handleEditorSelectMentionKnowledge,
    handleUpdateComment,
    handleDeleteComment,
    handleCancelDelete,
    handleConfirmDelete,
    deleteModalCommentId,
    deletingComment,
    removeEditingRule,
    removeEditingKnowledge,
  } = useCommentEditor({
    threadId: thread_id || "",
    setComments,
    activeHighlightedCommentId,
    setActiveHighlightedCommentId,
    onCommentDeleted: registerDeletedCommentId,
  });

  // 5. In-Place Composer Hook
  const {
    inPlaceComposer,
    composerText,
    composerRuleIds,
    composerKnowledgeIds,
    savingComposer,
    composerMentionType,
    composerMentionQuery,
    openTurnComposer,
    closeComposer,
    handleMessageMouseUp,
    handleComposerTextChange,
    handleSelectMentionRule: handleComposerSelectMentionRule,
    handleSelectMentionKnowledge: handleComposerSelectMentionKnowledge,
    removeComposerRule,
    removeComposerKnowledge,
    handleSaveComposerComment,
    handleAddAlteredNote,
  } = useInPlaceComposer({
    threadId: thread_id || "",
    activeBatchId,
    evaluations,
    setComments,
    onRequireActiveBatch: ensureActiveBatch,
    onCommentCreated: (tempId, newComment) => {
      if (activeHighlightedCommentId === tempId) {
        setActiveHighlightedCommentId(newComment.id);
      }
    },
  });

  // Click outside listener to close in-place composer
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        inPlaceComposer &&
        !target.closest("#in-place-comment-composer") &&
        !target.closest("[data-add-note-btn]") &&
        !target.closest("[data-add-altered-note-btn]")
      ) {
        const selection = window.getSelection();
        if (selection && !selection.isCollapsed && selection.toString().trim().length >= 2) {
          return;
        }
        closeComposer();
      }
    };
    if (inPlaceComposer) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [inPlaceComposer, closeComposer]);

  // Escape key handler to cancel editing / composer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (inPlaceComposer) closeComposer();
        if (editingCommentId) cancelEdit();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inPlaceComposer, editingCommentId, closeComposer, cancelEdit]);

  if (loading && !thread) {
    return (
      <div className="max-w-6xl mx-auto p-12 text-center text-stone-400 text-xs flex items-center justify-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin text-stone-500" />
        <span>Loading conversation stream...</span>
      </div>
    );
  }

  if (error || !thread) {
    return (
      <div className="max-w-6xl mx-auto space-y-4 p-8">
        <div className="p-8 text-center bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-2xl text-rose-600 dark:text-rose-400 text-xs">
          {error || "Thread not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 w-full overflow-hidden bg-stone-50 dark:bg-stone-950">
      {/* 1. Header Bar: Minimalist with Subtle Action Labels */}
      <ThreadHeader
        thread={thread}
        compositeScore={compositeScore}
        isFlagged={isFlagged}
        hasHallucination={hasHallucination}
        activeBatchId={activeBatchId}
        activeBatches={activeBatches}
        allBatches={allBatches}
        evaluations={evaluations}
        comments={comments}
        commentBatchFilter={commentBatchFilter}
        onSelectCommentBatchFilter={setCommentBatchFilter}
        telemetrySummary={telemetrySummary}
        loading={loading}
        isRefreshing={isRefreshing}
        onRefresh={() => loadThreadData(true)}
        onSelectBatchId={(id) => {
          setActiveBatchId(id);
          setTargetBatchId(id);
        }}
        onOpenBatchModal={() => setIsBatchAssignModalOpen(true)}
        onOpenSimulator={() => {
          setSimulatorTurnId(undefined);
          setSimulatorInquiry(undefined);
          setSimulatorBaseline(undefined);
          setIsSimulatorOpen(true);
        }}
        onOpenReviewDrawer={() => {
          setSelectedEvaluation(null);
          setEvalDrawerMessageId(null);
          setEvalDrawerRunId(null);
          if (activeBatches.length === 0) {
            ensureActiveBatch(() => {
              setEvalDrawerIsHumanMode(true);
              setIsEvalDrawerOpen(true);
            });
          } else {
            setEvalDrawerIsHumanMode(true);
            setIsEvalDrawerOpen(true);
          }
        }}
        onOpenEvaluationsList={() => {
          setSelectedEvaluation(null);
          setEvalDrawerMessageId(null);
          setEvalDrawerRunId(null);
          setEvalDrawerIsHumanMode(false);
          setIsEvalDrawerOpen(true);
        }}
      />

      {/* 2. Scrollable Messages Stream */}
      <ThreadMessagesStream
        messages={messages}
        evaluations={evaluations}
        comments={
          commentBatchFilter === "all"
            ? comments
            : commentBatchFilter === "unbatched"
            ? comments.filter((c) => !c.batch_id)
            : comments.filter((c) => c.batch_id === commentBatchFilter)
        }
        rulesList={referencedRules}
        guardrailsList={guardrailsList}
        knowledgeList={referencedKnowledge}
        activeBatches={activeBatches}
        allBatches={allBatches}
        agentInfo={agentInfo}
        activeHighlightedCommentId={activeHighlightedCommentId}
        editingCommentId={editingCommentId}
        editingText={editingText}
        editingRuleIds={editingRuleIds}
        editingKnowledgeIds={editingKnowledgeIds}
        savingEdit={savingEdit}
        mentionType={editorMentionType}
        mentionQuery={editorMentionQuery}
        isWaitingForAgent={isWaitingForAgent}
        inPlaceComposer={inPlaceComposer}
        composerText={composerText}
        composerRuleIds={composerRuleIds}
        composerKnowledgeIds={composerKnowledgeIds}
        savingComposer={savingComposer}
        composerMentionType={composerMentionType}
        composerMentionQuery={composerMentionQuery}
        activeBatch={activeBatch}
        onCloseComposer={closeComposer}
        onComposerTextChange={handleComposerTextChange}
        onSaveComposerComment={handleSaveComposerComment}
        onOpenBatchModal={() => ensureActiveBatch()}
        onComposerSelectMentionRule={handleComposerSelectMentionRule}
        onComposerSelectMentionKnowledge={handleComposerSelectMentionKnowledge}
        onComposerRemoveRule={removeComposerRule}
        onComposerRemoveKnowledge={removeComposerKnowledge}
        onSelectText={(msgId) => {
          ensureActiveBatch(() => {
            handleMessageMouseUp(msgId);
          });
        }}
        onOpenTurnComposer={(msgId, evalId) => {
          ensureActiveBatch(() => {
            openTurnComposer(msgId, evalId);
          });
        }}
        onOpenEvalDrawer={(evalItem, msgId, runId) => {
          setSelectedEvaluation(evalItem);
          setEvalDrawerMessageId(msgId);
          setEvalDrawerRunId(runId);
          setEvalDrawerIsHumanMode(false);
          setIsEvalDrawerOpen(true);
        }}
        onSimulateTurn={(msgId, baselineContent, inquiry) => {
          setSimulatorTurnId(msgId);
          setSimulatorBaseline(baselineContent);
          setSimulatorInquiry(inquiry);
          setIsSimulatorOpen(true);
        }}
        onSetActiveHighlightedCommentId={setActiveHighlightedCommentId}
        onStartEditComment={startEdit}
        onCancelEditComment={cancelEdit}
        onSaveEditComment={handleUpdateComment}
        onDeleteComment={handleDeleteComment}
        onEditTextChange={handleEditTextChange}
        onSelectMentionRule={handleEditorSelectMentionRule}
        onSelectMentionKnowledge={handleEditorSelectMentionKnowledge}
        onRemoveEditingRule={removeEditingRule}
        onRemoveEditingKnowledge={removeEditingKnowledge}
        onDismissWaiting={() => setIsWaitingForAgent(false)}
        onInspectRule={handleInspectRule}
        onInspectGuardrail={handleInspectGuardrail}
        onInspectKnowledge={handleInspectKnowledge}
        onInspectChunk={handleInspectChunk}
        runsMap={runsMap}
        onAddAlteredNote={handleAddAlteredNote}
      />

      {/* 3. Dedicated Separated Chat Input Bar at Bottom */}
      <ThreadInputBar
        inputText={inputText}
        placeholder={agentInfo.placeholder}
        sending={sending}
        isWaitingForAgent={isWaitingForAgent}
        hasComments={comments.length > 0}
        onInputChange={setInputText}
        onSendMessage={handleSendMessage}
      />

      {/* 4. Modals and Overlays */}
      <ThreadDetailModals
        thread={thread}
        scorecard={scorecard}
        evaluations={effectiveEvaluations}
        activeBatches={activeBatches}
        allBatches={allBatches}
        activeBatchId={activeBatchId}
        activeBatch={activeBatch || null}
        isActiveBatchModalOpen={isActiveBatchModalOpen}
        onCloseActiveBatchModal={handleCloseActiveBatchModal}
        onSelectActiveBatch={handleSelectActiveBatch}
        onCreateActiveBatch={handleCreateActiveBatch}
        isBatchAssignModalOpen={isBatchAssignModalOpen}
        targetBatchId={targetBatchId}
        newBatchTitle={newBatchTitle}
        creatingBatchInline={creatingBatchInline}
        assigningEvaluations={assigningEvaluations}
        assignAllEvaluations={assignAllEvaluationsInline}
        onSetAssignAllEvaluations={setAssignAllEvaluationsInline}
        onCloseBatchAssignModal={handleCloseBatchAssignModal}
        onSetTargetBatchId={(id) => {
          setTargetBatchId(id);
          setActiveBatchId(id);
        }}
        onSetNewBatchTitle={setNewBatchTitle}
        onSetCreatingBatchInline={setCreatingBatchInline}
        onAssignEvaluations={handleAssignEvaluationsToBatch}
        onOpenBatchAssignModal={openBatchAssignModal}
        onAssignDirectToActiveBatch={handleAssignDirectToActiveBatch}
        deleteModalCommentId={deleteModalCommentId}
        deletingComment={deletingComment}
        onCancelDeleteComment={handleCancelDelete}
        onConfirmDeleteComment={handleConfirmDelete}
        isEvalDrawerOpen={isEvalDrawerOpen}
        selectedEvaluation={selectedEvaluation}
        evalDrawerMessageId={evalDrawerMessageId}
        evalDrawerRunId={evalDrawerRunId}
        evalDrawerIsHumanMode={evalDrawerIsHumanMode}
        isSimulatorOpen={isSimulatorOpen}
        simulatorTurnId={simulatorTurnId}
        simulatorInquiry={simulatorInquiry}
        simulatorBaseline={simulatorBaseline}
        onCloseEvalDrawer={() => setIsEvalDrawerOpen(false)}
        onCloseSimulator={() => setIsSimulatorOpen(false)}
        onReviewSubmitted={() => loadThreadData(true)}
        onRequireActiveBatch={ensureActiveBatch}
        inspectionTarget={inspectionTarget}
        onCloseInspection={() => setInspectionTarget(null)}
        onInspectTarget={setInspectionTarget}
      />
    </div>
  );
}
